// These helpers are meant to be used by reducers that handle processing of standard QCS "sync" resources.
import type { UnknownAction } from '@reduxjs/toolkit';
import type { RecordOf } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import { assertInterface } from '../../utils/core';
import type { AsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import { undoActions, undoTypes } from '../undo';
import type { UndoProps } from '../undo/undoTypes';
import type { BaseResource, QcsSyncResourceStore, QcsSyncResourceStoreProps } from './resourceStoreTypes';
import type { CompareFunc } from './resourceUpserters';
import { upsertResource, upsertResources } from './resourceUpserters';

/** The meta of an action that can register an undo. */
type UndoMeta = { undo?: UndoProps };

/** An action that registers its meta's undo through the asyncDispatch the middleware adds. */
type UndoableAction<Payload, M extends UndoMeta> = {
  payload: Payload;
  meta?: M | null;
  asyncDispatch?: AsyncDispatch;
};

export function makeUndoResource<T extends BaseResource>(
  existingResource: RecordOf<T> | undefined | null,
  newResource: RecordOf<T>,
  afterUpdate?: boolean,
) {
  let undoResource: RecordOf<T> | undefined;
  if (
    newResource.isDeleted &&
    !afterUpdate &&
    assertInterface.assert(existingResource, 'undo delete: existing resource not found') &&
    existingResource
  ) {
    // action=delete => undoAction=create
    // Start with existing resource as base
    undoResource = existingResource;

    // Merge any explicitly set fields from the delete payload into the undo resource.
    // This allows callers to specify values that should be preserved on undo
    // Exclude structural fields that are managed separately.
    const structuralFields = new Set(['id', 'clientId', 'isDeleted', 'createdAt', 'modifiedAt']);
    const newResourceData: [string, unknown][] = Object.entries(newResource.toObject());
    const fieldsToMerge = newResourceData.filter(([key, value]) => !structuralFields.has(key) && value !== undefined);

    if (fieldsToMerge.length > 0) {
      undoResource = undoResource.merge(fieldsToMerge);
    }

    // Set up for create: remove id, add new clientId
    undoResource = undoResource.delete('id');
    undoResource = undoResource.set('clientId', uuidv4().toUpperCase());
  } else if (
    !existingResource &&
    (afterUpdate === undefined || afterUpdate) &&
    !newResource.isDeleted &&
    newResource.id
  ) {
    // action=create => undoAction=delete
    undoResource = newResource.set('isDeleted', true);
  } else if (!afterUpdate && existingResource) {
    // action=update => undoAction=update
    undoResource = existingResource;
  }

  return undoResource;
}

export function setupUndo(
  asyncDispatch: AsyncDispatch | undefined,
  undoConfig: UndoProps,
  executeActions?: UnknownAction[],
) {
  if (asyncDispatch && undoConfig?.userMessage) {
    const undo = undoTypes.mkUndo({
      actions: executeActions,
      userMessage: undoConfig.userMessage,
      suppressToast: undoConfig.suppressToast,
      deferToast: undoConfig.deferToast,
      // The undo meta object identifies the operation, so revealUndo can target this entry alone.
      deferToken: undoConfig.deferToast ? undoConfig : undefined,
      key: undoConfig.key ?? uuidv4(),
    });
    asyncDispatch(undoActions.addUndo(undo));
  }
}

export function setupUndoForAction(
  actionToUndo: UndoableAction<unknown, UndoMeta> | null | undefined,
  executeActions?: UnknownAction[],
) {
  if (actionToUndo?.asyncDispatch && actionToUndo?.meta?.undo?.userMessage) {
    setupUndo(actionToUndo.asyncDispatch, actionToUndo.meta.undo, executeActions);
  }
}

export function upsertResourceSetupUndo<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T>,
  M extends UndoMeta = UndoMeta,
>(
  state: QcsSyncResourceStore<T, V>,
  action: UndoableAction<RecordOf<T>, M>,
  undoActionCreator?: (resource: RecordOf<T>, meta: Omit<M, 'undo'>) => UnknownAction,
  comparator?: CompareFunc,
  retainDeleted = false,
) {
  if (action?.meta?.undo?.userMessage) {
    let undoResource: RecordOf<T> | undefined;
    if (!action.meta.undo.disableUndoAction && undoActionCreator && state.resourcesById) {
      const existingResource = action.payload.id ? state.resourcesById.get(action.payload.id) : undefined;
      undoResource = action.payload && makeUndoResource(existingResource, action.payload, undefined);
    }
    if (action.meta.undo.disableUndoAction || undoResource) {
      const { undo, ...meta } = action.meta;
      setupUndoForAction(action, undoActionCreator && undoResource ? [undoActionCreator(undoResource, meta)] : []);
    }
  }
  return upsertResource(state, action.payload, undefined, undefined, comparator, retainDeleted);
}

export function upsertResourcesSetupUndo<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T>,
  M extends UndoMeta = UndoMeta,
>(
  state: QcsSyncResourceStore<T, V>,
  action: UndoableAction<RecordOf<T>[], M>,
  undoActionCreator?: (resources: RecordOf<T>[], meta: Omit<M, 'undo'>) => UnknownAction,
  afterUpdate = false,
  comparator?: CompareFunc,
  skipOptimistic = false,
  retainDeleted = false,
) {
  const { payload: resources } = action;
  if (action?.meta?.undo?.userMessage) {
    const undoResources: RecordOf<T>[] = [];
    if (!action.meta.undo.disableUndoAction && undoActionCreator && state.resourcesById) {
      resources.forEach((resource) => {
        const existingResource = state.resourcesById.get(resource.id || '');
        const undoResource = makeUndoResource(existingResource, resource, afterUpdate);
        if (undoResource) {
          undoResources.push(undoResource);
        }
      });
    }
    if (!afterUpdate || undoResources?.length) {
      // don't trigger add undo after update, if no undo resources provided
      const { undo, ...meta } = action.meta;
      setupUndoForAction(
        action,
        undoActionCreator && undoResources?.length ? [undoActionCreator(undoResources, meta)] : [],
      );
    }
    if (afterUpdate && action.meta.undo.deferToast) {
      // The undo was registered when the batch was dispatched but held back; every write has landed
      // now, so it is safe to offer. Reveal after the setup above, since a create batch registers its
      // real undo from this pass and asyncDispatch flushes in call order.
      action.asyncDispatch?.(undoActions.revealUndo(action.meta.undo));
    }
  }

  if (afterUpdate || !skipOptimistic) {
    return upsertResources(state, { resources, doMerge: true }, comparator, retainDeleted);
  } else {
    return state;
  }
}
